/**
 * Central application store (Zustand).
 *
 * Holds all session state — imported points, matched pairs, the current
 * transformation solution, and blunder-detection results — plus the pure
 * actions that mutate it. Async work (the solver Web Worker) stays in
 * components/hooks; workers report their results back through actions here.
 */

import { create } from 'zustand';
import { createSafeStorage } from '@/utils/autosaveStorage';
import { validatePersistedState } from '@/utils/projectFile';
import { useAutosaveStatus } from './autosaveStatus';
import { persist, createJSONStorage } from 'zustand/middleware';
import { createPredictionStdDev, transformPoint } from '@/logic/adjustments';
import { WEIGHT, isInFit } from '@/logic/weights';
import { planTransform } from '@/utils/transformSelection';
import { autoMatchPoints } from '@/utils/pointMatching';
import { EXAMPLES } from '@/data/examples';
import type {
  FitSnapshot,
  RejectionTrialPreview,
} from '@/types/adjustmentReview';
import type {
  SystemPoint,
  PointPair,
  TransformationSolution,
  BlunderDetectionResults,
  DataSnoopingResults,
  TransformedPoint,
  ActiveTab,
  MatchingMode,
} from '@/types';
import {
  AUTOSAVE_VERSION,
  createDefaultPersistedState,
  pickAutosaveState,
  pickPersistedState,
  type ApplySnapshot,
  type DetectionMethod,
  type PersistedState,
} from './persistedState';

export {
  PERSISTED_KEYS,
  pickAutosaveState,
  pickPersistedState,
} from './persistedState';
export type {
  ApplySnapshot,
  AutosaveState,
  DetectionMethod,
  PersistedState,
} from './persistedState';

export interface AppStore extends PersistedState {
  // Transient UI state (not persisted)
  currentFit: FitSnapshot | null;
  previousFit: FitSnapshot | null;
  trialPreview: RejectionTrialPreview | null;
  beforeApplyFit: FitSnapshot | null;
  setTrialPreview: (preview: RejectionTrialPreview) => void;
  clearTrialPreview: () => void;
  replacementSnapshot: PersistedState | null;
  undoReplacement: () => void;
  showHelp: boolean;
  selectedPointId: number | null;

  // --- Point data actions ---
  loadSystemPoints: (points: SystemPoint[], systemNumber: 1 | 2) => void;
  /** Remove one dataset; like a replacement, it clears pairs and results. */
  clearSystemPoints: (systemNumber: 1 | 2) => void;
  matchPoints: (mode: MatchingMode) => void;
  acceptMatchedPairs: (
    pairs: PointPair[],
    mode: MatchingMode,
    detectedOffset?: number | null
  ) => void;
  addPointPair: () => void;
  updatePointPair: (
    id: number,
    field: keyof PointPair,
    value: string | number,
    autofill?: boolean
  ) => void;
  linkPoints: (sys1Point: string, sys2Point: string) => void;
  unlinkPair: (id: number) => void;
  setPointPairs: (pairs: PointPair[]) => void;
  setPointWeight: (id: number, weight: number) => void;
  resetAllWeights: () => void;

  // --- Solution actions ---
  commitSolution: (
    solution: TransformationSolution,
    expectedRevision: number,
    scale: number | null
  ) => void;
  setBlunderResults: (results: BlunderDetectionResults | null) => void;
  setDataSnoopingResults: (results: DataSnoopingResults | null) => void;
  setActiveDetectionMethod: (method: DetectionMethod) => void;
  applyRejections: (
    rejectedIds: number[],
    solution: TransformationSolution,
    expectedRevision: number,
    scale: number | null
  ) => void;
  undoLastApply: () => void;
  transformPoints: () => void;

  // --- Session actions ---
  loadExample: (key: string) => void;
  loadProject: (data: PersistedState) => void;
  reset: () => void;

  // --- UI setters ---
  setActiveTab: (tab: ActiveTab) => void;
  setShowHelp: (show: boolean) => void;
  setRejectionLevel: (level: number) => void;
  setViewAllSolutions: (viewAll: boolean) => void;
  setRunAllMaxLevel: (level: number) => void;
  setSelectedAlpha: (alpha: number) => void;
  setCustomScale: (scale: string) => void;
  setPointNumberOffset: (offset: number) => void;
  setTransformRange: (range: { from: string; to: string }) => void;
  setMatchingMode: (mode: MatchingMode) => void;
  setSelectedPointId: (id: number | null) => void;
  setUnits: (units: string) => void;
}

/**
 * Undo of an apply restores the complete pre-apply pairs, so it is only
 * offered until the inputs change again; afterwards it would silently discard
 * the user's later edits.
 */
/** Numeric value of a scale setting; blank or unparsable means "estimate". */
const scaleSettingValue = (text: string): number | null => {
  const value = text.trim() === '' ? NaN : Number(text);
  return Number.isFinite(value) && value > 0 ? value : null;
};

const discardApplyUndo = {
  lastApplySnapshot: null,
  beforeApplyFit: null,
} as const;

/**
 * Incremental edits (weights, coordinates, single pairs) keep the last
 * results on screen but flag them as stale so the user can compare and
 * re-solve deliberately.
 */
const staleResults = {
  trialPreview: null,
  transformedStale: true,
  solutionStale: true,
  combinatorialStale: true,
  dataSnoopingStale: true,
  ...discardApplyUndo,
} as const;

/**
 * Wholesale data replacement (new file, re-match, example load) makes old
 * results meaningless — clear everything.
 */
const clearedResults = {
  currentFit: null,
  previousFit: null,
  trialPreview: null,
  beforeApplyFit: null,
  transformedPoints: [] as TransformedPoint[],
  transformedFromRevision: null,
  transformedTimestamp: null,
  transformedStale: false,
  solutionInputRevision: null,
  appliedScale: null,
  solution: null,
  solutionStale: false,
  solutionTimestamp: null,
  blunderResults: null,
  dataSnoopingResults: null,
  combinatorialStale: false,
  dataSnoopingStale: false,
  activeDetectionMethod: null as DetectionMethod,
  lastApplySnapshot: null,
} as const;

/** Pair ids are positive integers; the first generated id is 1. */
const nextPairId = (pairs: PointPair[]): number =>
  Math.max(0, ...pairs.map((p) => p.id)) + 1;

/** Loaded current results can acquire a snapshot before their first edit. */
const acceptedFit = (
  state: PersistedState & { currentFit?: FitSnapshot | null }
): FitSnapshot | null =>
  state.currentFit ??
  (state.solution && !state.solution.error && !state.solutionStale
    ? { pointPairs: state.pointPairs, solution: state.solution }
    : null);

/**
 * Restoring saved or snapshotted state must never move `inputRevision`
 * backwards: a worker started before the restore carries the old revision
 * and must not be accepted against the restored inputs. Revisions are
 * session counters, so the restored data is re-based past the current one,
 * keeping the solved-input relationship intact.
 */
const rebaseRevisions = (
  data: PersistedState,
  currentRevision: number
): Pick<PersistedState, 'inputRevision' | 'solutionInputRevision'> => {
  const inputRevision = Math.max(currentRevision, data.inputRevision) + 1;
  const shift = inputRevision - data.inputRevision;
  return {
    inputRevision,
    solutionInputRevision:
      data.solutionInputRevision === null
        ? null
        : data.solutionInputRevision + shift,
  };
};

const transientData = {
  currentFit: null,
  previousFit: null,
  trialPreview: null,
  beforeApplyFit: null,
  selectedPointId: null,
};

/** Durable keys that `reset` returns to their defaults. */
const resetData = (): Omit<
  PersistedState,
  | 'rejectionLevel'
  | 'viewAllSolutions'
  | 'runAllMaxLevel'
  | 'selectedAlpha'
  | 'matchingMode'
  | 'activeTab'
> => {
  const {
    rejectionLevel: _rejectionLevel,
    viewAllSolutions: _viewAllSolutions,
    runAllMaxLevel: _runAllMaxLevel,
    selectedAlpha: _selectedAlpha,
    matchingMode: _matchingMode,
    activeTab: _activeTab,
    ...data
  } = createDefaultPersistedState();
  return data;
};

/**
 * Rehydrate browser autosave through the same validation as project files.
 * Data that fails validation (or is absent) leaves the fresh session intact.
 */
const mergeAutosave = <S extends PersistedState>(
  persisted: unknown,
  current: S
): S => {
  const result = validatePersistedState(persisted, AUTOSAVE_VERSION);
  return result.ok ? { ...current, ...result.data } : current;
};

/**
 * Version-aware autosave migration. Version 0/1 sessions lack result
 * provenance, so their results are marked for recomputation (see
 * `validatePersistedState`). Newer versions written by a later app are read
 * on a best-effort basis: inputs that validate are kept and every retained
 * result is treated as unverifiable.
 */
const migrateAutosave = (persisted: unknown, version: number): unknown => {
  const result = validatePersistedState(
    persisted,
    version > AUTOSAVE_VERSION ? 1 : version
  );
  return result.ok ? result.data : null;
};

export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      ...createDefaultPersistedState(),
      ...transientData,
      replacementSnapshot: null,
      showHelp: false,

      // --- Point data actions ---

      loadSystemPoints: (points, systemNumber) => {
        if (!points.length) return;
        const state = get();
        const system1Points = systemNumber === 1 ? points : state.system1Points;
        const system2Points = systemNumber === 2 ? points : state.system2Points;

        set({
          system1Points,
          system2Points,
          pointPairs: [],
          detectedOffset: null,
          replacementSnapshot: pickPersistedState(get()),
          ...clearedResults,
          inputRevision: get().inputRevision + 1,
        });
      },

      clearSystemPoints: (systemNumber) => {
        const state = get();
        const points =
          systemNumber === 1 ? state.system1Points : state.system2Points;
        if (!points.length) return;
        set({
          ...(systemNumber === 1
            ? { system1Points: [] }
            : { system2Points: [] }),
          pointPairs: [],
          detectedOffset: null,
          replacementSnapshot: pickPersistedState(state),
          ...clearedResults,
          inputRevision: state.inputRevision + 1,
        });
      },

      matchPoints: (mode) => {
        const { system1Points, system2Points } = get();
        if (system1Points.length === 0 || system2Points.length === 0) return;

        const result = autoMatchPoints(system1Points, system2Points, mode);
        set({
          pointPairs: result.pairs,
          matchingMode: result.mode,
          detectedOffset: result.detectedOffset ?? null,
          replacementSnapshot: pickPersistedState(get()),
          ...clearedResults,
          inputRevision: get().inputRevision + 1,
        });
      },

      acceptMatchedPairs: (pairs, mode, detectedOffset = null) => {
        set({
          replacementSnapshot: pickPersistedState(get()),
          pointPairs: pairs,
          matchingMode: mode,
          detectedOffset,
          ...clearedResults,
          inputRevision: get().inputRevision + 1,
          activeTab: 'pairs',
          selectedPointId: null,
        });
      },

      // Deliberately does not mark results stale: an empty pair is excluded from
      // the fit until its coordinates are filled in, and updatePointPair marks
      // staleness at that moment.
      addPointPair: () => {
        const { pointPairs } = get();
        const newId = nextPairId(pointPairs);
        set({
          trialPreview: null,
          ...discardApplyUndo,
          currentFit: acceptedFit(get()),
          pointPairs: [
            ...pointPairs,
            {
              id: newId,
              label: `PT${newId}`,
              sys1Point: '',
              sys2Point: '',
              n1: '',
              e1: '',
              n2: '',
              e2: '',
              weight: WEIGHT.unity,
            },
          ],
        });
      },

      updatePointPair: (id, field, value, autofill = true) => {
        const { pointPairs, system1Points, system2Points } = get();
        set({
          pointPairs: pointPairs.map((p) => {
            if (p.id !== id) return p;

            const updated = { ...p, [field]: value };

            // Auto-fill coordinates when point number changes
            if (autofill && field === 'sys1Point' && value) {
              const match = system1Points.find((sp) => sp.point === value);
              if (match) {
                updated.n1 = match.north;
                updated.e1 = match.east;
                updated.desc1 = match.description;
                updated.label = String(value);
              }
            }
            if (autofill && field === 'sys2Point' && value) {
              const match = system2Points.find((sp) => sp.point === value);
              if (match) {
                updated.n2 = match.north;
                updated.e2 = match.east;
                updated.desc2 = match.description;
              }
            }

            return updated;
          }),
          ...staleResults,
          currentFit: acceptedFit(get()),
          inputRevision: get().inputRevision + 1,
        });
      },

      linkPoints: (sys1Point, sys2Point) => {
        const { system1Points, system2Points, pointPairs } = get();
        const s1 = system1Points.find((sp) => sp.point === sys1Point);
        const s2 = system2Points.find((sp) => sp.point === sys2Point);
        if (!s1 || !s2) return;

        set({
          pointPairs: [
            ...pointPairs,
            {
              id: nextPairId(pointPairs),
              label: `${sys1Point}-${sys2Point}`,
              sys1Point,
              sys2Point,
              n1: s1.north,
              e1: s1.east,
              n2: s2.north,
              e2: s2.east,
              weight: WEIGHT.unity,
              desc1: s1.description,
              desc2: s2.description,
            },
          ],
          ...staleResults,
          currentFit: acceptedFit(get()),
          inputRevision: get().inputRevision + 1,
        });
      },

      unlinkPair: (id) => {
        set({
          pointPairs: get().pointPairs.filter((p) => p.id !== id),
          ...staleResults,
          currentFit: acceptedFit(get()),
          inputRevision: get().inputRevision + 1,
        });
      },

      setPointPairs: (pairs) => {
        set({
          replacementSnapshot: pickPersistedState(get()),
          pointPairs: pairs,
          ...clearedResults,
          inputRevision: get().inputRevision + 1,
        });
      },

      setPointWeight: (id, weight) => {
        set({
          pointPairs: get().pointPairs.map((p) =>
            p.id === id ? { ...p, weight } : p
          ),
          ...staleResults,
          currentFit: acceptedFit(get()),
          inputRevision: get().inputRevision + 1,
        });
      },

      resetAllWeights: () => {
        set({
          pointPairs: get().pointPairs.map((p) => ({
            ...p,
            weight: WEIGHT.unity,
          })),
          ...staleResults,
          currentFit: acceptedFit(get()),
          inputRevision: get().inputRevision + 1,
        });
      },

      // --- Solution actions ---

      setTrialPreview: (preview) => {
        const state = get();
        const scale = state.customScale ? Number(state.customScale) : null;
        if (
          preview.inputRevision !== state.inputRevision ||
          preview.appliedScale !== scale ||
          preview.solution.error ||
          (state.blunderResults && state.combinatorialStale) ||
          (state.dataSnoopingResults && state.dataSnoopingStale)
        )
          return;
        set({ trialPreview: preview });
      },
      clearTrialPreview: () => set({ trialPreview: null }),

      commitSolution: (solution, expectedRevision, scale) => {
        if (get().inputRevision !== expectedRevision) return;
        set({
          previousFit: acceptedFit(get()),
          currentFit: { pointPairs: get().pointPairs, solution },
          trialPreview: null,
          solution,
          solutionStale: false,
          solutionTimestamp: Date.now(),
          solutionRevision: get().solutionRevision + 1,
          solutionInputRevision: expectedRevision,
          appliedScale: scale,
          transformedStale: true,
        });
      },

      setBlunderResults: (results) =>
        set({
          trialPreview: null,
          blunderResults: results,
          ...(results ? { combinatorialStale: false } : {}),
        }),
      setDataSnoopingResults: (results) =>
        set({
          trialPreview: null,
          dataSnoopingResults: results,
          ...(results ? { dataSnoopingStale: false } : {}),
        }),
      setActiveDetectionMethod: (method) =>
        set({ activeDetectionMethod: method }),

      applyRejections: (rejectedIds, result, expectedRevision, scale) => {
        if (
          !rejectedIds.length ||
          expectedRevision !== get().inputRevision ||
          result.error
        )
          return;
        if (
          (get().blunderResults && get().combinatorialStale) ||
          (get().dataSnoopingResults && get().dataSnoopingStale)
        )
          return;

        const {
          pointPairs,
          solution,
          solutionStale,
          solutionTimestamp,
          blunderResults,
          dataSnoopingResults,
          combinatorialStale,
          dataSnoopingStale,
          activeDetectionMethod,
        } = get();

        const snapshot: ApplySnapshot = {
          appliedScale: get().appliedScale,
          customScale: get().customScale,
          pointPairs,
          solution,
          solutionStale,
          solutionTimestamp,
          blunderResults,
          dataSnoopingResults,
          combinatorialStale,
          dataSnoopingStale,
          activeDetectionMethod,
        };

        const updatedPairs = pointPairs.map((p) =>
          rejectedIds.includes(p.id) ? { ...p, weight: WEIGHT.free } : p
        );

        set({
          previousFit: acceptedFit(get()),
          beforeApplyFit: acceptedFit(get()),
          currentFit: { pointPairs: updatedPairs, solution: result },
          trialPreview: null,
          inputRevision: get().inputRevision + 1,
          pointPairs: updatedPairs,
          solution: result,
          solutionStale: false,
          solutionTimestamp: Date.now(),
          solutionRevision: get().solutionRevision + 1,
          solutionInputRevision: get().inputRevision + 1,
          appliedScale: scale,
          transformedStale: true,
          blunderResults: null,
          dataSnoopingResults: null,
          combinatorialStale: false,
          dataSnoopingStale: false,
          activeDetectionMethod: null,
          lastApplySnapshot: snapshot,
        });
      },

      undoLastApply: () => {
        const { lastApplySnapshot } = get();
        if (!lastApplySnapshot) return;
        set({
          ...lastApplySnapshot,
          previousFit: acceptedFit(get()),
          currentFit:
            get().beforeApplyFit ??
            (lastApplySnapshot.solution && !lastApplySnapshot.solutionStale
              ? {
                  pointPairs: lastApplySnapshot.pointPairs,
                  solution: lastApplySnapshot.solution,
                }
              : null),
          beforeApplyFit: null,
          trialPreview: null,
          inputRevision: get().inputRevision + 1,
          solutionRevision: get().solutionRevision + 1,
          solutionInputRevision: get().inputRevision + 1,
          transformedStale: true,
          lastApplySnapshot: null,
        });
      },

      transformPoints: () => {
        const { solution, transformRange, pointNumberOffset, system1Points } =
          get();
        if (!solution || solution.error || get().solutionStale) return;

        const plan = planTransform(
          system1Points,
          transformRange,
          pointNumberOffset
        );
        if (plan.error) return;
        const predictStdDev = createPredictionStdDev(
          get().pointPairs,
          solution
        );
        const results: TransformedPoint[] = plan.points.map(
          ({ source: p, name }) => {
            const n1 = Number(p.north);
            const e1 = Number(p.east);
            const transformed = transformPoint(n1, e1, solution);
            return {
              originalPoint: p.point,
              newPoint: name,
              n1: p.north,
              e1: p.east,
              // Export precision: four decimals (see file-formats guide).
              n2: transformed.n2.toFixed(4),
              e2: transformed.e2.toFixed(4),
              // Blank when the solution has no redundancy to estimate σ̂₀.
              stdDev: predictStdDev ? predictStdDev(n1, e1).toFixed(3) : '',
              description: p.description,
            };
          }
        );

        set({
          transformedPoints: results,
          activeTab: 'transform',
          transformedStale: false,
          transformedFromRevision: get().solutionRevision,
          transformedTimestamp: Date.now(),
        });
      },

      // --- Session actions ---

      loadExample: (key) => {
        const example = EXAMPLES.find((e) => e.key === key);
        if (!example) return;
        set({
          system1Points: example.sys1,
          system2Points: example.sys2,
          pointPairs: example.pairs,
          activeTab: 'pairs',
          replacementSnapshot: pickPersistedState(get()),
          ...clearedResults,
          inputRevision: get().inputRevision + 1,
        });
      },

      loadProject: (data) => {
        set({
          replacementSnapshot: pickPersistedState(get()),
          ...data,
          ...rebaseRevisions(data, get().inputRevision),
          ...transientData,
          currentFit: acceptedFit(data),
          showHelp: false,
        });
      },

      reset: () => {
        set({
          ...resetData(),
          ...transientData,
          replacementSnapshot: pickPersistedState(get()),
          inputRevision: get().inputRevision + 1,
          activeTab: 'import',
        });
      },

      undoReplacement: () => {
        const snapshot = get().replacementSnapshot;
        if (!snapshot) return;
        set({
          ...snapshot,
          ...rebaseRevisions(snapshot, get().inputRevision),
          ...transientData,
          currentFit: acceptedFit(snapshot),
          replacementSnapshot: null,
        });
      },

      // --- UI setters ---

      setUnits: (units) => set({ units }),
      setActiveTab: (tab) => set({ activeTab: tab }),
      setShowHelp: (show) => set({ showHelp: show }),
      setRejectionLevel: (level) => set({ rejectionLevel: level }),
      setViewAllSolutions: (viewAll) => set({ viewAllSolutions: viewAll }),
      setRunAllMaxLevel: (level) => set({ runAllMaxLevel: level }),
      setSelectedAlpha: (alpha) => set({ selectedAlpha: alpha }),
      setCustomScale: (scale) =>
        set((state) => ({
          customScale: scale,
          trialPreview: null,
          ...discardApplyUndo,
          // Detection trials hold the scale setting; results computed under a
          // different scale no longer describe the fit an apply would make.
          ...(scaleSettingValue(scale) !== scaleSettingValue(state.customScale)
            ? { combinatorialStale: true, dataSnoopingStale: true }
            : {}),
        })),
      setPointNumberOffset: (offset) =>
        set({ pointNumberOffset: offset, transformedStale: true }),
      setTransformRange: (range) =>
        set({ transformRange: range, transformedStale: true }),
      setMatchingMode: (mode) => set({ matchingMode: mode }),
      setSelectedPointId: (id) => set({ selectedPointId: id }),
    }),
    {
      name: 'resolve-session',
      version: AUTOSAVE_VERSION,
      migrate: migrateAutosave,
      merge: mergeAutosave,
      storage: createJSONStorage(() =>
        createSafeStorage((status) => useAutosaveStatus.setState({ status }))
      ),
      partialize: (state) => pickAutosaveState(state),
    }
  )
);

/** Pairs that participate in the least-squares fit. */
export const selectActivePointCount = (state: {
  pointPairs: PointPair[];
}): number =>
  state.pointPairs.filter(
    (p) =>
      isInFit(p.weight) &&
      p.n1 !== '' &&
      p.e1 !== '' &&
      p.n2 !== '' &&
      p.e2 !== ''
  ).length;
